import { useLayoutEffect } from "react";
import { useIsMounted } from "./useIsMounted";

export type UsePointerEventOptions = {
	ignoreWheelEvent?: boolean;
	ignoreTouchMoveEvent?: boolean;
	shouldListenForEntireClick?: boolean;
	otherRoots?: readonly (HTMLElement | nil)[];
};

export function usePointerEventOutside(
	callback: (() => void) | nil,
	firstRoot: (HTMLElement | nil),
	options: UsePointerEventOptions = {}
): void {
	const isMounted: () => boolean = useIsMounted();
	const {
		ignoreWheelEvent = false,
		ignoreTouchMoveEvent = false,
		shouldListenForEntireClick = true,
		otherRoots
	} = options;

	useLayoutEffect(
		() => {
			const roots: (HTMLElement | nil)[] = [
				firstRoot
			];

			if (otherRoots) {
				roots.push(...otherRoots);
			}

			const onGlobalEvent = (args: MouseEvent | WheelEvent | TouchEvent) => {
				if (!callback) {
					return;
				}

				const interactedElement: HTMLElement = args.target as HTMLElement;

				const canWeDetermineWhetherItWasInsideOrOutside: boolean = window.document.contains(interactedElement);
				if (!canWeDetermineWhetherItWasInsideOrOutside) {
					return;
				}

				const isInteractedElementChildOfAnyRoot: boolean = roots.some(
					root =>
						!root
						|| root === interactedElement
						|| root.contains(interactedElement)
				);

				if (isInteractedElementChildOfAnyRoot) {
					return;
				}

				// We don't directly trigger the handler because:
				// 1. We are in the capture phase
				// 2. These events are not dispatched from the react system and result in react re-rendering
				// the component directly
				// Especially with a "toggle" behavior what would happen is that the event handler of the
				// "toggle handler" (bubble) would operate on the new state caused by the handler
				// resulting in that the DropOver would be "toggled" again (i.e. true => false => true)
				setTimeout(
					() => {
						if (isMounted()) {
							callback();
						}
					},
					0
				);
			};

			const leftClickEvent: keyof DocumentEventMap = shouldListenForEntireClick
				? "click"
				: "mousedown";

			document.addEventListener(leftClickEvent, onGlobalEvent, { capture: true });
			document.addEventListener("contextmenu", onGlobalEvent, { capture: true });
			if (!ignoreTouchMoveEvent) {
				document.addEventListener("touchmove", onGlobalEvent, { capture: true });
			}
			if (!ignoreWheelEvent) {
				document.addEventListener("wheel", onGlobalEvent, { capture: true });
			}

			return () => {
				document.removeEventListener(leftClickEvent, onGlobalEvent, { capture: true });
				document.removeEventListener("contextmenu", onGlobalEvent, { capture: true });
				if (!ignoreTouchMoveEvent) {
					document.removeEventListener("touchmove", onGlobalEvent, { capture: true });
				}
				if (!ignoreWheelEvent) {
					document.removeEventListener("wheel", onGlobalEvent, { capture: true });
				}
			};
		},
		[
			callback,
			isMounted,
			firstRoot,
			otherRoots,
			ignoreWheelEvent,
			ignoreTouchMoveEvent,
			shouldListenForEntireClick
		]
	);
}